import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { checkCampaignCode, checkCampaignJoined, createUserCampaign } from "../../../api/api";
import RegisterPage from "../Register/index";
import Cookies from "js-cookie";
import { Box, Typography, Button, Card, CardContent, Container } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HomeIcon from "@mui/icons-material/Home";

const CampaignEntry = () => {
  const { campaignCode } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isValid, setIsValid] = useState(false);
  const [userLoggedIn, setUserLoggedIn] = useState(false);
  const [alreadyJoined, setAlreadyJoined] = useState(false);
  const [campaignExpired, setCampaignExpired] = useState(false);
  const [error, setError] = useState("");

  // Function to translate backend error messages to Norwegian
  const translateErrorMessage = (message) => {
    if (!message) return "Denne kampanjen har utløpt og er ikke lenger tilgjengelig.";
    
    const translations = {
      'Campaign signup period has ended. Signup end date is finished.': 'Kampanje påmeldingsperioden har utløpt. Påmeldingsfristen er over.',
      'Campaign usage limit reached. This campaign is no longer available.': 'Kampanje bruksgrensen er nådd. Denne kampanjen er ikke lenger tilgjengelig.',
      'Campaign not found': 'Kampanje ikke funnet',
      'No company linked with this campaign': 'Ingen bedrift er koblet til denne kampanjen'
    };
    
    return translations[message] || message;
  };

  useEffect(() => {
    const validateCampaign = async () => {
      console.log("campaignCode:", campaignCode);
      
      // Check if user is already logged in
      const userId = Cookies.get("user_id");
      const companyId = Cookies.get("company_id");
      
      if (userId && companyId) {
        console.log("User is already logged in, checking campaign status...");
        setUserLoggedIn(true);
        
        try {
          // Check if user already joined this campaign
          const joinedRes = await checkCampaignJoined({
            user_id: userId,
            campaign_code: campaignCode,
          });

          if (joinedRes.data.joined) {
          //  console.log("User has already joined this campaign");
            setAlreadyJoined(true);
            setLoading(false);
            return;
          }

          // If not joined, join the campaign directly
        //  console.log("User has NOT joined yet, joining campaign...");
          await createUserCampaign({
            user_id: userId,
            campaign_code: campaignCode,
          });

         // console.log("Campaign joined successfully, redirecting to home");
          navigate("/user_home", {
            state: { company_id: companyId, message: "Kampanje lagt til!" }
          });
          return;
        } catch (err) {
          console.error("Error checking/joining campaign:", err);
          
          // Handle 403 Forbidden (campaign expired/ended)
          if (err.response?.status === 403) {
            const errorMsg = err.response.data.error || err.response.data.message || "";
            // Check if it's an "already joined" message
            if (errorMsg.includes('already') || errorMsg.includes('joined') || errorMsg.includes('deltatt')) {
              setAlreadyJoined(true);
              setLoading(false);
              return;
            }
            // Otherwise treat as expired
            setCampaignExpired(true);
            setError(translateErrorMessage(errorMsg || "Denne kampanjen har utløpt."));
          } else if (err.response?.data?.message?.includes('expired') || 
                     err.response?.data?.message?.includes('ended') ||
                     err.response?.data?.error?.includes('expired') ||
                     err.response?.data?.error?.includes('ended')) {
            setCampaignExpired(true);
            const errorMsg = err.response.data.error || err.response.data.message;
            setError(translateErrorMessage(errorMsg));
          } else if (err.response?.data?.message?.includes('already') || 
                     err.response?.data?.message?.includes('joined') ||
                     err.response?.data?.message?.includes('deltatt') ||
                     err.response?.data?.error?.includes('already') ||
                     err.response?.data?.error?.includes('joined') ||
                     err.response?.data?.error?.includes('deltatt')) {
            // User has already joined
            setAlreadyJoined(true);
            setLoading(false);
            return;
          } else {
            // For authentication errors or other issues, redirect to login with message
            if (err.response?.status === 401 || err.response?.status === 403) {
              navigate("/", { 
                state: { 
                  error: "Du har allerede deltatt i denne kampanjen! Vennligst logg inn for å se dine kuponger." 
                } 
              });
              return;
            }
            setError("En feil oppstod. Vennligst prøv igjen.");
          }
          setLoading(false);
          return;
        }
      }

      // If user is not logged in, validate campaign normally
      try {
        const res = await checkCampaignCode(campaignCode);

        if (!res.data.valid) {
          const errorMessage = res.data.message || "Ugyldig kampanjekode.";
          
          // Check if it's a signup end date issue
          if (errorMessage.includes('signup') || errorMessage.includes('ended') || errorMessage.includes('finished')) {
            setCampaignExpired(true);
            setError(translateErrorMessage(errorMessage));
            setLoading(false);
            return;
          }
          
          navigate("/", { state: { error: errorMessage } });
          return;
        }

        localStorage.setItem("campaignCode", campaignCode);
        setIsValid(true);
      } catch (err) {
        console.error("Campaign check failed", err);
        // Check if error indicates user already joined
        const errorMsg = err.response?.data?.message || err.response?.data?.error || "";
        if (errorMsg.includes('already') || errorMsg.includes('joined') || errorMsg.includes('deltatt')) {
          navigate("/", {
            state: { error: "Du har allerede deltatt i denne kampanjen! Vennligst logg inn for å se dine kuponger." },
          });
        } else {
          navigate("/", {
            state: { error: "Noe gikk galt. Prøv igjen senere." },
          });
        }
      } finally {
        setLoading(false);
      }
    };

    validateCampaign();
  }, [campaignCode, navigate]);

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontSize: '18px',
        color: '#666'
      }}>
        {userLoggedIn ? 'Legger til kampanje...' : 'Validerer kampanjekode...'}
      </div>
    );
  }

  // Show already joined message
  if (alreadyJoined) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Box textAlign="center">
          <CheckCircleIcon 
            sx={{ 
              fontSize: 80, 
              color: 'success.main', 
              mb: 2 
            }} 
          />
          <Typography variant="h5" gutterBottom sx={{ color: 'success.main', fontWeight: 'bold' }}>
            Du har allerede deltatt i denne kampanjen!
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
            Du har allerede fått tilgang til kupongene fra denne kampanjen. 
            Sjekk dine kuponger på hjemmesiden.
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<HomeIcon />}
            onClick={() => navigate("/user_home", { 
              state: { company_id: Cookies.get("company_id") } 
            })}
            sx={{ px: 4, py: 1.5 }}
          >
            Gå til Mine Kuponger
          </Button>
        </Box>
      </Container>
    );
  }

  // Show campaign expired message
  if (campaignExpired) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Box textAlign="center">
          <Typography 
            variant="h5" 
            gutterBottom 
            sx={{ color: 'error.main', fontWeight: 'bold' }}
          >
            Kampanje Utløpt
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
            {error || "Denne kampanjen har utløpt og er ikke lenger tilgjengelig."}
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<HomeIcon />}
            onClick={() => navigate("/user_home", { 
              state: { company_id: Cookies.get("company_id") } 
            })}
            sx={{ px: 4, py: 1.5 }}
          >
            Gå til Mine Kuponger
          </Button>
        </Box>
      </Container>
    );
  }

  // If user is logged in, don't show register page
  if (userLoggedIn) return null;

  if (isValid) return <RegisterPage campaignCode={campaignCode} />;

  return null;
};

export default CampaignEntry;
